import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { inviteAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './AcceptInvite.css';

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    loadInvite();
  }, [token]);

  const loadInvite = async () => {
    try {
      const res = await inviteAPI.getByToken(token);
      setInvite(res.data.invite);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired invite');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!user) {
      // Save token and redirect to login, then back to this invite page
      localStorage.setItem('pendingInvite', token);
      navigate(`/login?redirect=/invite/${token}`);
      return;
    }

    setAccepting(true);
    try {
      const res = await inviteAPI.accept(token);
      navigate(`/family/${res.data.familyCircle._id}`);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to accept invite';
      // If already a member, just redirect to the family circle
      if (errorMsg.includes('already a member') && invite?.familyCircle?._id) {
        navigate(`/family/${invite.familyCircle._id}`);
        return;
      }
      setError(errorMsg);
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="accept-invite-page">
        <div className="invite-card">
          <div className="spinner-large"></div>
          <p>Loading invite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="accept-invite-page">
        <div className="invite-card error">
          <div className="invite-icon">❌</div>
          <h2>Invite Error</h2>
          <p>{error}</p>
          <button className="btn-primary" onClick={() => navigate('/')}>
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="accept-invite-page">
      <div className="invite-card">
        <div className="invite-icon">🎉</div>
        <h2>You're Invited!</h2>
        
        <p className="invite-message">
          <strong>{invite.invitedBy?.displayName}</strong> has invited you to join
        </p>
        
        <h3 className="family-name">{invite.familyCircle?.name}</h3>
        
        <div className="invite-details">
          <div className="detail-row">
            <span className="label">Your role:</span>
            <span className="value">{invite.role}</span>
          </div>
          {invite.relationship && (
            <div className="detail-row">
              <span className="label">Relationship:</span>
              <span className="value">{invite.relationship}</span>
            </div>
          )}
        </div>

        {!user && (
          <p className="login-notice">
            You'll need to log in or create an account to accept this invite.
          </p>
        )}

        <button 
          className="btn-accept"
          onClick={handleAccept}
          disabled={accepting}
        >
          {accepting ? 'Joining...' : user ? 'Accept & Join' : 'Log In to Accept'}
        </button>

        <p className="expires-notice">
          This invite expires on {new Date(invite.expiresAt).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
};

export default AcceptInvite;